import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import { AppDispatch } from '../../../../store/store';
import ChatComponent from '../../Сhat/Chat';
import { getMessageHistory, getMessagesForTopic, saveMessagesForTopic } from '../../../../utils/MessageHistory.js';
import { setStatusAnalyzeFile } from '../../../../store/slices/uploadAndAnalyzeAI.js';
import {
  handleSummarizeClick,
  handleOptimizeClick,
  handleCheckboxChange,
  handleBtnAskClick,
} from '../../../../utils/OptimizerTable/index';
import { optimizeCosts, askChat } from '../../../../fetch/axios.js';
import { OptCostsResult, OptimizeData, RowItem } from '../../../../types/optimizerTable';
import OptimizerButton from './OptimizerButton';
import OptimizerTableHead from './OptimizerTableHead';
import OptimizerOption from './OptimizerOption';
import {
  CustomTableContainer,
  CustomTable,
  CustomTableBody,
  CustomTableRow,
  CustomTableCell,
  TableTitle,
  AveragePrice,
  Title,
  ContentInner,
  Content,
  GridContainer,
  GridItem,
  CustomCard,
  CustomCardContent,
  TotalText,
  TotalTextLineThrough,
  CustomCardContentInner,
  ButtonsContainer,
} from './OptimizerTable.styles';
export type RowData = RowItem[];
export default function OptimizerTable({ data }: { data: RowData | null }) {
  const { t } = useTranslation('optimizer');
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isSummarizeButtonDisabled, setIsSummarizeButtonDisabled] = React.useState(true);
  const [isSummarizeLoading, setIsSummarizeLoading] = React.useState(false);
  const [openAlternatives, setOpenAlternatives] = React.useState<{ [key: number]: boolean }>({});
  const [chatState, setChatState] = React.useState<{
    isOpen: boolean;
    topic: string;
  }>({
    isOpen: false,
    topic: '',
  });
  const [isLoading, setIsLoading] = React.useState<Record<string, boolean>>({});
  const [isOptimizeLoading, setIsOptimizeLoading] = React.useState(false);
  const [isOptimizeButtonDisabled, setIsOptimizeButtonDisabled] = React.useState(true);
  const prevTotal = React.useMemo(() => {
    if (!data) return 0;
    return Math.round(
      data.reduce(
        (accumulator, { averagePrice }) => accumulator + +averagePrice,
        0,
      ) * 100,
    ) / 100;
  }, [data]);
  const [optimizeData, setOptimizeData] = React.useState<OptimizeData[]>(() => {
    if (!data) return [];
    return data.map(({ id, title, averagePrice, optimizationOptions }) => ({
      id, title, averagePrice,
      optimizationOptions: optimizationOptions?.map(({ id, title, isChat, optCostsResults, findings }) => ({
        id,
        title,
        isChat,
        findings,
        optCostsResults: (optCostsResults as OptCostsResult[] | undefined)?.map(
          ({ id, title, contact, price, enabled }) => ({
            id,
            title,
            contact,
            price,
            enabled,
          })),
      })),
    }));
  });
  const total = React.useMemo(() => {
    return optimizeData.reduce((accumulator, { averagePrice, optimizationOptions }) => {
      let priceNum = +averagePrice;
      optimizationOptions?.forEach(({ optCostsResults }) => {
        optCostsResults?.forEach(({ price, enabled }) => {
          if (enabled) {
            priceNum = price;
          }
        });
      });
      return Math.round((accumulator + priceNum) * 100) / 100;
    }, 0);
  }, [optimizeData]);
  const percentageSaved = React.useMemo(() => {
    if (prevTotal === 0) return 0;
    const savings = prevTotal - total;
    return Math.round((savings / prevTotal) * 100 * 100) / 100;
  }, [prevTotal, total]);
  React.useEffect(() => {
    const savings = prevTotal - total;
    const summaryData = {
      prevTotal: prevTotal,
      total: total,
      saving: savings,
      percentageSaved: percentageSaved,
    };
    localStorage.setItem('priceSummary', JSON.stringify(summaryData));
  }, [prevTotal, total]);
  React.useEffect(() => {
    const checkButtonState = () => {
      const hasSelectedOptions = optimizeData.some(({ optimizationOptions }) =>
        optimizationOptions?.some(({ optCostsResults }) =>
          optCostsResults && optCostsResults.length > 0,
        ),
      );
      const messageHistory = getMessageHistory();
      const hasChats = messageHistory && Object.keys(messageHistory).length > 0;
      const hasOptimizationOptions = optimizeData.some(({ optimizationOptions }) =>
        optimizationOptions?.some(option => option.enabled),
      );
      setIsSummarizeButtonDisabled(!(hasSelectedOptions || hasChats));
      setIsOptimizeButtonDisabled(!hasOptimizationOptions);
    };
    checkButtonState();
  }, [optimizeData]);
  const handleOpenChat = (topic: string) => {
    setChatState({
      isOpen: true,
      topic,
    });
  };
  const handleCloseChat = () => {
    setChatState(prev => ({
      ...prev,
      isOpen: false,
    }));
  };
  const [isButtonsFixed, setIsButtonsFixed] = React.useState(false);
  const buttonsRef = React.useRef<HTMLDivElement>(null);
  const buttonsSentinelRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleScroll = () => {
      if (buttonsSentinelRef.current) {
        const rect = buttonsSentinelRef.current.getBoundingClientRect();
        const shouldBeFixed = rect.top > window.innerHeight;
        setIsButtonsFixed(shouldBeFixed);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  React.useEffect(() => {
    const event = new CustomEvent('bottomPanelFixed', { detail: { isFixed: isButtonsFixed } });
    window.dispatchEvent(event);
  }, [isButtonsFixed]);
  return (
    <Box sx={{ minHeight: '100vh', paddingBottom: '80px', position: 'relative' }}>
      <Title>
        {t('table.selectOptions')}
      </Title>
      <CustomTableContainer>
        <CustomTable>
          <OptimizerTableHead />
          <CustomTableBody>
            {optimizeData.map(({ id, title, averagePrice, optimizationOptions }) => (
              <React.Fragment key={id}>
                <CustomTableRow>
                  <CustomTableCell colSpan={4}>
                    <Content>
                      <ContentInner>
                        <TableTitle>{title}</TableTitle>
                      </ContentInner>
                      <ContentInner>
                        <AveragePrice>{averagePrice}$</AveragePrice>
                      </ContentInner>
                    </Content>
                    {optimizationOptions && optimizationOptions.length > 0 && (
                      <>
                        <GridContainer>
                          {optimizationOptions.map((opt, index) => (
                            <GridItem key={opt.id}>
                              <OptimizerOption
                                opt={opt}
                                setOpenAlternatives={setOpenAlternatives}
                                openAlternatives={openAlternatives}
                                handleBtnAskClick={handleBtnAskClick}
                                handleCheckboxChange={handleCheckboxChange}
                                optimizeData={optimizeData}
                                setOptimizeData={setOptimizeData}
                                id={id}
                                title={title}
                                averagePrice={averagePrice}
                                handleOpenChat={handleOpenChat}
                                getMessagesForTopic={getMessagesForTopic}
                                saveMessagesForTopic={saveMessagesForTopic}
                                askChat={askChat}
                                isOpen={true}
                                loading={isLoading}
                                setLoading={setIsLoading}
                                isLast={index === optimizationOptions.length - 1}
                              />
                            </GridItem>
                          ))}
                        </GridContainer>
                      </>
                    )}
                  </CustomTableCell>
                </CustomTableRow>
              </React.Fragment>
            ))}
          </CustomTableBody>
        </CustomTable>
      </CustomTableContainer>
      <Box ref={buttonsSentinelRef} sx={{ height: 0, visibility: 'hidden' }} />
      <ButtonsContainer
        ref={buttonsRef}
        sx={{
          position: isButtonsFixed ? 'fixed' : 'relative',
          boxShadow: isButtonsFixed ? '0px -2px 5px 0px rgba(0,0,0,0.5)' : 'none',
        }}
      >
        <Container
          maxWidth="lg"
          disableGutters
          sx={{
            boxShadow: 'none',
            borderTop: 'none',
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <CustomCard>
            <CustomCardContent sx={{ gap: isButtonsFixed ? 0 : '8px' as string }}>
              <CustomCardContentInner>
                <TotalText variant="h6"><strong>{t('table.total')}</strong></TotalText>
                {prevTotal !== total && (
                  <TotalTextLineThrough color="success.main">
                    {prevTotal}$
                  </TotalTextLineThrough>
                )}
                <TotalText color="success.main">
                  {total}$
                </TotalText>
              </CustomCardContentInner>
              {!!(prevTotal - total) && (
                <CustomCardContentInner>
                  <TotalText variant="h6" color="black">
                    <strong>{t('table.savings')}:</strong>
                  </TotalText>
                  <TotalText variant="h6" color="success.main">
                    +{prevTotal - total}$
                  </TotalText>
                </CustomCardContentInner>
              )}
            </CustomCardContent>
          </CustomCard>
          {!isOptimizeButtonDisabled && (
            <OptimizerButton
              isOptimizerButtonDisabled={isSummarizeButtonDisabled}
              isOptimizerButtonLoading={isSummarizeLoading}
              handleOptimizerButtonClick={() =>
                handleSummarizeClick({ optimizeData, setIsSummarizeLoading, navigate })}
              text={t('table.summarize')}
            />
          )}
          <OptimizerButton
            isOptimizerButtonDisabled={isOptimizeButtonDisabled}
            isOptimizerButtonLoading={false}
            handleOptimizerButtonClick={() =>
              handleOptimizeClick({
                optimizeData,
                setIsOptimizeLoading,
                setOptimizeData,
                dispatch,
                setOpenAlternatives,
                optimizeCosts,
                setStatusAnalyzeFile,
                openAlternatives,
              })
            }
            sx={{
              marginLeft: 1,
            }}
            text={t('table.optimize')}
            endIcon={<AutofpsSelectIcon />}
            isOptimizeLoading={isOptimizeLoading}
          />
        </Container>
      </ButtonsContainer>
      <ChatComponent
        topic={chatState.topic}
        isOpen={chatState.isOpen}
        onClose={handleCloseChat}
      />
    </Box>
  );
}
